import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Search, X, Trash2, Plus, QrCode, BarChart3 } from "lucide-react";
import AnalyticsTab from "@/components/AnalyticsTab";
import MediaInput from "@/components/MediaInput";
import ExcelTools from "@/components/ExcelTools";
import AuditLogList from "@/components/AuditLogList";
import { ClientDashboardView, ClientStatusBoardView, ClientLeadsView } from "./ClientPortalViews";

export default function ClientProject() {
  const { pid } = useParams();
  const [project, setProject] = useState(null);
  const [groups, setGroups] = useState([]);
  const [entities, setEntities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [etapFilter, setEtapFilter] = useState("all");
  const [blockFilter, setBlockFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("dashboard");
  const [leads, setLeads] = useState([]);

  const load = async () => {
    try {
      const [p, g, e, a, l] = await Promise.all([
        api.get(`/client/projects/${pid}`),
        api.get(`/client/projects/${pid}/groups`),
        api.get(`/client/projects/${pid}/entities`),
        api.get(`/client/projects/${pid}/analytics`),
        api.get(`/client/projects/${pid}/leads`),
      ]);
      setProject(p.data); setGroups(g.data); setEntities(e.data); setAnalytics(a.data); setLeads(l.data || []);
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  // ----- Hierarchy helpers --------------------------------------------------
  const etaps = useMemo(() =>
    groups.filter((g) => g.group_type === "etap").sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
  [groups]);
  const blocks = useMemo(() => {
    const real = groups.filter((g) => g.group_type === "block");
    const legacy = groups.filter((g) => !g.group_type && g.parent_group_id == null);
    return [...real, ...legacy].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [groups]);
  const etapById = useMemo(() => Object.fromEntries(etaps.map((e) => [e.id, e])), [etaps]);
  const blockById = useMemo(() => Object.fromEntries(blocks.map((b) => [b.id, b])), [blocks]);
  const visibleBlocks = etapFilter === "all" ? blocks : blocks.filter((b) => b.parent_group_id === etapFilter);
  const blockPath = (b) => {
    if (!b) return "—";
    const e = etapById[b.parent_group_id];
    return e ? `${e.name} › ${b.name}` : b.name;
  };

  const filtered = useMemo(() => entities
    .filter((e) => statusFilter === "all" || e.status === statusFilter)
    .filter((e) => {
      if (blockFilter !== "all") return e.group_id === blockFilter;
      if (etapFilter !== "all") {
        const b = blockById[e.group_id];
        return !!b && b.parent_group_id === etapFilter;
      }
      return true;
    })
    .filter((e) => !search || (e.code + " " + e.name).toLowerCase().includes(search.toLowerCase())),
  [entities, search, statusFilter, etapFilter, blockFilter, blockById]);

  const save = async (form) => {
    try {
      const body = { ...form };
      if (typeof body.gallery_urls === "string") {
        body.gallery_urls = body.gallery_urls.split(/\n+/).map(s => s.trim()).filter(Boolean);
      }
      await api.put(`/client/entities/${form.id}`, body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (!project) return <div className="px-8 py-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="px-8 py-6 max-w-7xl">
      <Link to="/portal" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="back-to-portal"><ArrowLeft className="h-3 w-3" /> All projects</Link>
      <div className="mt-3 mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight" data-testid="portal-project-title">{project.name}</h1>
          <div className="text-xs text-zinc-500 mt-1">/{project.slug}</div>
        </div>
        <div className="inline-flex bg-zinc-100 rounded-lg p-1 text-sm flex-wrap">
          <button
            onClick={() => setView("dashboard")}
            data-testid="portal-view-dashboard"
            className={`px-3 py-1.5 rounded-md transition ${view === "dashboard" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
          >Dashboard</button>
          <button
            onClick={() => setView("status")}
            data-testid="portal-view-status"
            className={`px-3 py-1.5 rounded-md transition ${view === "status" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
          >Status board</button>
          <button
            onClick={() => setView("units")}
            data-testid="portal-view-units"
            className={`px-3 py-1.5 rounded-md transition ${view === "units" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
          >Units</button>
          <button
            onClick={() => setView("leads")}
            data-testid="portal-view-leads"
            className={`px-3 py-1.5 rounded-md transition ${view === "leads" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
          >Leads</button>
          <button
            onClick={() => setView("analytics")}
            data-testid="portal-view-analytics"
            className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition ${view === "analytics" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
          ><BarChart3 className="h-3.5 w-3.5" /> Analytics</button>
        </div>
      </div>

      {view === "dashboard" && (
        <ClientDashboardView
          project={project}
          entities={entities}
          leads={leads}
          analytics={analytics}
          onJumpView={setView}
        />
      )}

      {view === "status" && (
        <ClientStatusBoardView entities={entities} groups={groups} onReload={load} />
      )}

      {view === "leads" && (
        <ClientLeadsView leads={leads} entities={entities} pid={pid} />
      )}

      {view === "analytics" && (
        <AnalyticsTab projectId={pid} endpointBase="/client" />
      )}

      {view === "units" && analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            ["Units", analytics.total_units],
            ["Available", analytics.available],
            ["Reserved", analytics.reserved],
            ["Sold", analytics.sold],
            ["Leads", analytics.leads],
          ].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-zinc-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{l}</div>
              <div className="font-display text-2xl mt-1" data-testid={`metric-${l.toLowerCase()}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {view === "units" && (
      <div className="rounded-xl border border-zinc-200 bg-white">
        <div className="px-4 py-3 border-b border-zinc-200 flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or name" className="pl-9 pr-3 py-1.5 rounded-md border border-zinc-300 text-sm w-64" data-testid="units-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-1.5 rounded-md border border-zinc-300 text-sm" data-testid="units-status-filter">
            <option value="all">All statuses</option>
            <option value="available">Available</option><option value="reserved">Reserved</option>
            <option value="sold">Sold</option><option value="hidden">Hidden</option>
          </select>
          <select
            value={etapFilter}
            onChange={(e) => { setEtapFilter(e.target.value); setBlockFilter("all"); }}
            className="px-3 py-1.5 rounded-md border border-zinc-300 text-sm"
            data-testid="units-etap-filter"
          >
            <option value="all">All Etaps</option>
            {etaps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <select
            value={blockFilter}
            onChange={(e) => setBlockFilter(e.target.value)}
            className="px-3 py-1.5 rounded-md border border-zinc-300 text-sm"
            data-testid="units-block-filter"
          >
            <option value="all">All Blocks</option>
            {visibleBlocks.map((b) => <option key={b.id} value={b.id}>{blockPath(b)}</option>)}
          </select>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs text-zinc-500">{filtered.length} units</div>
            <ExcelTools projectId={pid} onAfterImport={load} />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left px-5 py-3">Code</th><th className="text-left px-5 py-3">Etap › Block</th><th className="text-left px-5 py-3">Floor</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">Price</th><th className="text-left px-5 py-3">Updated</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="8" className="px-5 py-10 text-center text-zinc-500" data-testid="empty-portal-units">No units match.</td></tr>}
            {filtered.map(e => (
              <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => setEditing({ ...e, gallery_urls: (e.gallery_urls || []).join("\n") })} data-testid={`portal-unit-row-${e.id}`}>
                <td className="px-5 py-3 font-mono text-xs">{e.code}</td>
                <td className="px-5 py-3 text-zinc-600">{blockPath(blockById[e.group_id])}</td>
                <td className="px-5 py-3 text-zinc-600">{e.floor ?? "—"}</td>
                <td className="px-5 py-3 text-zinc-600">{e.type}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-2 py-1 rounded ${statusColor(e.status)}`}>{e.status}</span>
                </td>
                <td className="px-5 py-3 text-zinc-600">{e.fields?.["Price (USD)"] || e.fields?.Price || e.fields?.price || "—"}</td>
                <td className="px-5 py-3 text-zinc-500 text-xs">{e.updated_at ? new Date(e.updated_at).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-3 text-right text-xs text-zinc-400">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/public/projects/${project.slug}/qr?unit=${encodeURIComponent(e.code)}`}
                    download={`qr-${project.slug}-${e.code}.png`}
                    onClick={(ev) => ev.stopPropagation()}
                    title="Download QR"
                    className="inline-flex p-1 hover:bg-zinc-100 rounded mr-1"
                    data-testid={`portal-qr-${e.code}`}
                  >
                    <QrCode className="h-3.5 w-3.5" />
                  </a>
                  Edit
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {editing && <EditUnit form={editing} setForm={setEditing} onSave={() => save(editing)} blockPath={blockPath} blockById={blockById} pid={pid} />}

      <div className="mt-8">
        <div className="text-sm font-medium text-zinc-700 mb-3">Recent activity</div>
        <AuditLogList scope="client" projectId={pid} pageSize={50} />
      </div>
    </div>
  );
}

function statusColor(s) {
  switch (s) {
    case "available": return "bg-emerald-100 text-emerald-800";
    case "reserved": return "bg-amber-100 text-amber-800";
    case "sold": return "bg-red-100 text-red-800";
    default: return "bg-zinc-100 text-zinc-600";
  }
}

function EditUnit({ form, setForm, onSave, blockPath, blockById, pid }) {
  const setField = (key, val) => setForm({ ...form, fields: { ...form.fields, [key]: val } });
  const addField = () => setForm({ ...form, fields: { ...form.fields, ["Field " + (Object.keys(form.fields || {}).length + 1)]: "" } });
  const renameField = (oldKey, newKey) => {
    const next = { ...(form.fields || {}) };
    const v = next[oldKey]; delete next[oldKey]; next[newKey] = v;
    setForm({ ...form, fields: next });
  };
  const removeField = (k) => { const next = { ...(form.fields || {}) }; delete next[k]; setForm({ ...form, fields: next }); };

  // Client users can edit inventory data (status, price, media, custom
  // fields, description) but structural attributes — Unit Code and the
  // parent Block — are owned by the Platform Admin and surfaced read-only.
  // This prevents accidental reparenting that would silently break Excel
  // round-trips and zone targeting.
  const path = blockPath(blockById[form.group_id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
      <div className="bg-white w-full max-w-3xl rounded-xl p-6 max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="unit-edit-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{path}</div>
            <div className="font-display text-2xl font-mono" data-testid="unit-edit-code">{form.code}</div>
          </div>
          <button onClick={() => setForm(null)} className="p-2 hover:bg-zinc-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Name"><input className="i" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="unit-edit-name" /></Lbl>
          <Lbl label="Block (read-only)">
            <input className="i bg-zinc-50 text-zinc-500" value={path} readOnly data-testid="unit-edit-block-readonly" />
            <div className="text-[11px] text-zinc-400 mt-1">Structural assignments are managed by the project admin.</div>
          </Lbl>
          <Lbl label="Status">
            <select className="i" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="unit-edit-status">
              <option value="available">Available</option><option value="reserved">Reserved</option>
              <option value="sold">Sold</option><option value="hidden">Hidden</option>
              <option value="coming_soon">Coming soon</option><option value="featured">Featured</option>
            </select>
          </Lbl>
          <Lbl label="Type"><input className="i" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} /></Lbl>
          <Lbl label="Plan"><MediaInput projectId={pid} value={form.plan_url || ""} onChange={(v) => setForm({ ...form, plan_url: v })} accept="image/*" preserveOriginal testid="unit-edit-plan" /></Lbl>
          <Lbl label="Video"><MediaInput projectId={pid} value={form.video_url || ""} onChange={(v) => setForm({ ...form, video_url: v })} accept="video/*" testid="unit-edit-video" /></Lbl>
          <div className="col-span-2"><Lbl label="Gallery URLs (one per line)"><textarea className="i h-24" value={form.gallery_urls} onChange={(e) => setForm({ ...form, gallery_urls: e.target.value })} /></Lbl></div>
          <div className="col-span-2"><Lbl label="Description"><textarea className="i h-20" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Lbl></div>
          <div className="col-span-2">
            <div className="text-xs text-zinc-700 mb-1.5">Custom fields</div>
            <div className="space-y-2">
              {Object.entries(form.fields || {}).map(([k, v], i) => (
                <div key={i} className="flex gap-2">
                  <input value={k} onChange={(e) => renameField(k, e.target.value)} className="i flex-1" />
                  <input value={String(v)} onChange={(e) => setField(k, e.target.value)} className="i flex-1" data-testid={`unit-edit-field-${k}`} />
                  <button onClick={() => removeField(k)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={addField} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Add field</button>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={() => setForm(null)} className="px-4 py-2 rounded-md border border-zinc-300 text-sm">Cancel</button>
          <button onClick={onSave} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm" data-testid="unit-edit-save"><Save className="h-4 w-4" /> Save</button>
        </div>
        <style>{`.i { width:100%; padding: 0.5rem 0.75rem; border:1px solid #d4d4d8; border-radius: 6px; outline:none; background:#fff; }`}</style>
      </div>
    </div>
  );
}

function Lbl({ label, children }) {
  return <label className="block"><div className="text-xs text-zinc-700 mb-1.5">{label}</div>{children}</label>;
}
